import { Flex, Text } from '@chakra-ui/react'
import React, { useEffect, useMemo, useState } from 'react'

import { TooltipCell, NormalTextCellRenderer } from '@/components/Table/components/Cell/ToolTipCell'
import { sumArrayProperty } from '@/utils/arrayHelper'
import { isNumeric, numberWithCommas, roundNumber } from '@/utils/stringHelper'
import { useWatch } from 'react-hook-form'
import EditableTable from '@/components/Table/EditableTable'
import { PriceCell } from '@/components/Table/components/Cell'
import { getTotalContractPrice } from '@/features/offers/helpers/getTotalContractPrice'
import { createColumnHelper } from '@tanstack/react-table'
import { getStage } from '@/features/offers/helpers'
import { OfferStage } from '@/features/offers/constants'

const CostStructureForm = () => {
  const subSystems = useWatch({ name: 'subSystems' })
  const status = useWatch({ name: 'status' })

  const columnHelper = createColumnHelper()
  const totalContractPrice = getTotalContractPrice(subSystems, getStage(status) === OfferStage.BOOKING)

  const costStructureData = useMemo(() => {
    const costStructure = subSystems.reduce(
      (accData, curSubSystem) => {
        if (!curSubSystem?.costStructure) {
          return accData
        }
        return {
          GC_BAU: accData.GC_BAU + curSubSystem.costStructure.GC_BAU,
          GC_FS: accData.GC_FS + curSubSystem.costStructure.GC_FS,
          GC_OTHER: accData.GC_OTHER + curSubSystem.costStructure.GC_OTHER,
          material: accData.material + curSubSystem.costStructure.material,
          internalLabour: accData.internalLabour + curSubSystem.costStructure.internalLabour,
          internalLabourPercentage:
            accData.internalLabourPercentage + curSubSystem.costStructure.internalLabourPercentage,
          subcontractor: accData.subcontractor + curSubSystem.costStructure.subcontractor,
          freelancer: accData.freelancer + curSubSystem.costStructure.freelancer,
          other: accData.other + curSubSystem.costStructure.other,
          otherExpense: accData.otherExpense + curSubSystem.costStructure.otherExpense,
          misc: accData.misc + curSubSystem.costStructure.misc,
        }
      },
      {
        GC_BAU: 0,
        GC_FS: 0,
        GC_OTHER: 0,
        material: 0,
        internalLabour: 0,
        internalLabourPercentage: 0,
        subcontractor: 0,
        freelancer: 0,
        other: 0,
        otherExpense: 0,
        misc: 0,
      }
    )

    return [
      {
        label: 'GC-BAU',
        value: costStructure.GC_BAU || 0,
        percentage: totalContractPrice !== 0 ? roundNumber((costStructure.GC_BAU / totalContractPrice) * 100) : 0,
      },

      {
        label: 'GC-FS',
        value: costStructure.GC_FS || 0,
        percentage: totalContractPrice !== 0 ? roundNumber((costStructure.GC_FS / totalContractPrice) * 100) : 0,
      },

      {
        label: 'GC-Other',
        value: costStructure.GC_OTHER || 0,
        percentage: totalContractPrice !== 0 ? roundNumber((costStructure.GC_OTHER / totalContractPrice) * 100) : 0,
      },

      {
        label: '3P material',
        value: costStructure.material || 0,
        percentage: totalContractPrice !== 0 ? roundNumber((costStructure.material / totalContractPrice) * 100) : 0,
      },

      {
        label: 'Internal Labour',
        value: costStructure.internalLabour || 0,
        percentage:
          totalContractPrice !== 0 ? roundNumber((costStructure.internalLabour / totalContractPrice) * 100) : 0,
      },
      {
        label: 'Subcontractor',
        value: costStructure.subcontractor || 0,
        percentage:
          totalContractPrice !== 0 ? roundNumber((costStructure.subcontractor / totalContractPrice) * 100) : 0,
      },
      {
        label: 'Other Support',
        value: costStructure.freelancer || 0,
        percentage: totalContractPrice !== 0 ? roundNumber((costStructure.freelancer / totalContractPrice) * 100) : 0,
      },

      {
        label: 'Other Expense',
        value: costStructure.otherExpense || 0,
        percentage: totalContractPrice !== 0 ? roundNumber((costStructure.otherExpense / totalContractPrice) * 100) : 0,
      },
      {
        label: 'Misc',
        value: costStructure.misc || 0,
        percentage: totalContractPrice !== 0 ? roundNumber((costStructure.misc / totalContractPrice) * 100) : 0,
      },
    ]
  }, [totalContractPrice])

  const columnDef = useMemo(
    () => [
      columnHelper.accessor('label', {
        header: () => <>Cost Summary</>,
      }),
      columnHelper.accessor('value', {
        header: () => <>HKD</>,

        cell: PriceCell,
      }),
      columnHelper.accessor('percentage', {
        cell: ({ getValue }) => {
          return <TooltipCell text={`${getValue()}%`}></TooltipCell>
        },
      }),
    ],
    [columnHelper]
  )

  return (
    <Flex p={5} overflowX="scroll" gap={3}>
      <EditableTable columnDef={columnDef} data={costStructureData} disabled={true} tableWidth="100%" />
    </Flex>
  )
}

export default CostStructureForm
